/**
 * Menu Component
 * Dropdown menu with keyboard navigation
 */

import { useState, useEffect, useRef, useCallback, memo } from 'react'
import MenuItem, { MenuItemConfig } from './MenuItem'

interface MenuProps {
  label: string
  items: MenuItemConfig[]
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
}

function Menu({ label, items, isOpen, onToggle, onClose }: MenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  // Get all actionable items (skip separators and disabled items)
  const getActionableItems = useCallback(() => {
    return items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.type !== 'separator' && !item.disabled)
  }, [items])

  // Handle keyboard navigation within dropdown
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return

    const actionableItems = getActionableItems()
    const currentFocusIndex = actionableItems.findIndex(({ index }) => index === focusedIndex)

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (currentFocusIndex < actionableItems.length - 1) {
          setFocusedIndex(actionableItems[currentFocusIndex + 1].index)
        } else {
          setFocusedIndex(actionableItems[0].index)
        }
        break

      case 'ArrowUp':
        e.preventDefault()
        if (currentFocusIndex > 0) {
          setFocusedIndex(actionableItems[currentFocusIndex - 1].index)
        } else {
          setFocusedIndex(actionableItems[actionableItems.length - 1].index)
        }
        break

      case 'Enter':
      case ' ':
        e.preventDefault()
        if (focusedIndex >= 0) {
          const item = items[focusedIndex]
          if (item.action && !item.disabled) {
            item.action()
            onClose()
          }
        }
        break

      case 'Escape':
        e.preventDefault()
        onClose()
        break
    }
  }, [isOpen, focusedIndex, items, onClose, getActionableItems])

  // Setup keyboard listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    // Add a small delay to prevent immediate closing when opening
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 10)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Reset focused index when closing
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1)
    }
  }, [isOpen])

  return (
    <div className="menu" ref={menuRef}>
      <button
        className={`menu-trigger ${isOpen ? 'active' : ''}`}
        onClick={onToggle}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {label}
      </button>

      {isOpen && (
        <div className="menu-dropdown" role="menu">
          {items.map((item, index) => (
            <MenuItem
              key={`${item.label}-${index}`}
              item={item}
              onClose={onClose}
              onMouseEnter={() => setFocusedIndex(index)}
              onMouseLeave={() => setFocusedIndex(-1)}
              isFocused={focusedIndex === index}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default memo(Menu)
